import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getWecomConfig } from '@/api/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wecomEnabled, setWecomEnabled] = useState(false);

  useEffect(() => {
    getWecomConfig()
      .then((cfg) => setWecomEnabled(cfg.enabled))
      .catch(() => setWecomEnabled(false));
  }, []);

  const handleWecomLogin = async () => {
    try {
      const cfg = await getWecomConfig();
      if (!cfg.enabled) return;
      const state = Math.random().toString(36).slice(2);
      sessionStorage.setItem('wecom_state', state);
      const url =
        `https://login.work.weixin.qq.com/wwlogin/sso/login?login_type=CorpApp` +
        `&appid=${encodeURIComponent(cfg.corpId)}` +
        `&agentid=${encodeURIComponent(cfg.agentId)}` +
        `&redirect_uri=${encodeURIComponent(cfg.redirectUri)}` +
        `&state=${state}`;
      window.location.href = url;
    } catch {
      setError('企业微信登录暂不可用');
    }
  };

  // Already logged in — redirect to home
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('请填写邮箱和密码');
      return;
    }

    setLoading(true);
    try {
      await login({ email: email.trim(), password });
      navigate('/');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '登录失败，请重试';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
              <i className="ri-leaf-line text-xl text-background-50"></i>
            </div>
            <span className="font-heading font-bold text-2xl text-foreground-900">双碳CRM</span>
          </Link>
          <p className="mt-2 text-sm text-foreground-500">登录您的账户，开始管理客户资源</p>
        </div>

        {/* Login card */}
        <div className="bg-background-50 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                邮箱
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入邮箱"
                  className="w-full h-11 pl-10 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                密码
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full h-11 pl-10 pr-10 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400" />
                <span className="text-sm text-foreground-600">记住我</span>
              </label>
              <a href="#" className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
                忘记密码？
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  登录中...
                </>
              ) : (
                '登录'
              )}
            </button>
          </form>

          {wecomEnabled && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-background-200"></div>
                <span className="text-xs text-foreground-400">或</span>
                <div className="flex-1 h-px bg-background-200"></div>
              </div>
              <button
                type="button"
                onClick={handleWecomLogin}
                className="w-full h-11 border border-background-200 hover:bg-background-100 text-foreground-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-wechat-line text-base text-[#07C160]"></i>
                企业微信登录
              </button>
            </>
          )}

          <div className="mt-6 text-center">
            <span className="text-sm text-foreground-500">还没有账户？</span>
            <Link to="/register" className="text-sm text-primary-600 hover:text-primary-700 font-medium ml-1 transition-colors">
              立即注册
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}